import parsePercent from '@src/utility/parsePercent';

import { Element } from '../models/Element';
import { LayoutSegmentFactory, Scene } from '../models/layout';
import { creatomateToNormalizedCrop } from '../models/layout/CropTransformUtils';
import { mapFitTypeToTemplateId } from '../models/layout/legacyLayoutMapping';
import { Track } from '../models/Track';

import type { VideoElement } from '../models/elements/VideoElement';
import type { CropTransformKeyframe } from '../models/layout/types';
import type { Timeline } from '../models/Timeline';

/**
 * Service for initializing LayoutSegments on tracks that need them
 * Handles VideoElement tracks only
 */
export class SceneInitializationService {
  /**
   * Gets the target element for fit property - only returns VideoElement
   */
  private static getTargetElementForFit(element: Element): VideoElement | null {
    // Only support direct video elements
    if (Element.isVideoElement(element)) {
      return element as VideoElement;
    }

    return null;
  }

  /**
   * Extracts crop from element's position/size properties (x, y, width, height).
   * These are Creatomate-style center-based percentage coordinates.
   * Returns undefined if the element has default positioning (no crop adjustment needed).
   */
  private static extractCropFromElement(
    element: VideoElement,
  ): CropTransformKeyframe['crop'] | undefined {
    const x = parsePercent(element.x) ?? 50;
    const y = parsePercent(element.y) ?? 50;
    const width = parsePercent(element.width) ?? 100;
    const height = parsePercent(element.height) ?? 100;

    // If all values are defaults, no custom crop needed
    if (x === 50 && y === 50 && width === 100 && height === 100) {
      return undefined;
    }

    // Convert Creatomate positioning to normalized crop
    return creatomateToNormalizedCrop(x, y, width, height);
  }

  static ensureScenesInitialized(
    timeline: Timeline,
    trackId?: string,
  ): Scene[] {
    // only create scenes for video track with given trackId or all tracks
    const tracksArray = trackId
      ? timeline.tracks.filter(track => track.id === trackId)
      : timeline.tracks;
    const existingScenes = timeline.scenes;
    const newScenes: Scene[] = [];

    for (const track of tracksArray) {
      if (
        track.elements.length === 0 ||
        // supporting track means created by a scene
        Track.isSupporting(track.id)
      ) {
        continue;
      }

      // For each element on the track, check if it has a scene
      for (const element of track.elements) {
        // Check if there's already a scene anchored to this element
        const hasScene = existingScenes.some(scene => {
          const anchorStart =
            typeof scene.anchorStart === 'object' &&
            'elementId' in scene.anchorStart
              ? scene.anchorStart.elementId
              : null;
          const anchorEnd =
            typeof scene.anchorEnd === 'object' &&
            'elementId' in scene.anchorEnd
              ? scene.anchorEnd.elementId
              : null;

          return anchorStart === element.id || anchorEnd === element.id;
        });

        if (hasScene) {
          continue; // Skip elements that already have scenes
        }

        // Check if this element is valid for scene creation
        const targetElement = this.getTargetElementForFit(element);
        if (!targetElement) {
          continue; // Skip elements without fit property
        }

        const fitType = targetElement.fit ?? 'cover';
        const templateId = mapFitTypeToTemplateId(fitType);

        // Extract crop from element's existing position/size if set
        const crop = this.extractCropFromElement(targetElement);

        // Create layout with crop (if element has custom positioning)
        const layout = LayoutSegmentFactory.create(templateId, track.id, crop);

        const scene = new Scene({
          anchorStart: {
            elementId: element.id,
            relativeTime: null,
            end: false,
          },
          anchorEnd: {
            elementId: element.id,
            relativeTime: null,
            end: true,
          },
          layout,
        });

        newScenes.push(scene);
      }
    }

    return [...existingScenes, ...newScenes];
  }
}
